import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Package, 
  Calendar, 
  DollarSign, 
  FileText,
  User,
  Clock
} from 'lucide-react';
import { purchaseReturnsAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';
import TotalsBar from '../ui/TotalsBar';

const PurchaseReturnDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();

  // State
  const [purchaseReturn, setPurchaseReturn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch purchase return data
  const fetchPurchaseReturn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await purchaseReturnsAPI.get(id);
      setPurchaseReturn(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load purchase return on mount
  useEffect(() => {
    fetchPurchaseReturn();
  }, [id]);


  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Purchase Return</h3>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => navigate('/purchase-returns')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Purchase Returns
          </button>
        </div>
      </div>
    );
  }

  if (!purchaseReturn) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Purchase Return Not Found</h3>
          <p className="text-gray-600 mb-4">The purchase return you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/purchase-returns')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Purchase Returns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/purchase-returns')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Purchase Return #{purchaseReturn.ID}
            </h1>
            <p className="text-gray-600">Purchase return details</p>
          </div>
        </div>
        
      </div>

      <TotalsBar details={purchaseReturn.details} />

      {/* Purchase Return Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-semibold text-gray-900">Purchase Return Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Return Date</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {purchaseReturn.PurchaseReturnDate ? dayjs(purchaseReturn.PurchaseReturnDate).format('MMM DD, YYYY') : '-'}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{'Counter Sale'}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {purchaseReturn.CreatedDate ? dayjs(purchaseReturn.CreatedDate).format('MMM DD, YYYY h:mm A') : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {purchaseReturn.Remarks && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-700">Remarks</h3>
            </div>
            <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{purchaseReturn.Remarks}</p>
          </div>
        )}
      </div>

      {/* Purchase Return Items */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-semibold text-gray-900">Returned Items</h2>
        </div>

        {purchaseReturn.details && purchaseReturn.details.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {purchaseReturn.details.map((detail, index) => {
                  const itemTotal = (parseFloat(detail.Price) - parseFloat(detail.Discount || 0)) * parseFloat(detail.Quantity);
                  return (
                    <tr key={detail.ID || index} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {detail.item?.Name || 'Unknown Item'}
                          </div>
                          {detail.item?.Barcode && (
                            <div className="text-xs text-gray-500">Barcode: {detail.item.Barcode}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {detail.Quantity}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {formatPrice(detail.Price)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {formatPrice(detail.Discount || 0)}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {formatPrice(itemTotal)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {detail.Remarks || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No items in this purchase return</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default PurchaseReturnDetail;
